import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import { STEPS } from '../commonStateTaxData';
import { basicInfoQuestions, sharedExemptionQuestions } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import * as S from '../../styles';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function LegacyForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  const basicInfoForm = useRef();
  const basicInfoInitQ = getInitialQuestions(basicInfoQuestions);
  const [basicInfo, setBasicInfo] = useState(basicInfoInitQ);

  const exemptionsForm = useRef();

  const [numDependents, setNumDependents] = useState(data.dependents || '');
  const [numDependentsError, setNumDependentsError] = useState(false);
  const [numDependentsValueError, setNumDependentsValueError] = useState(false);

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(
    data.is_exempt || '',
  );

  const [personalExemption, setPersonalExemption] = useState(
    data.personal_exemption || '',
  );
  const [personalExemptionError, setPersonalExemptionError] = useState(false);

  const [husbandAge65, setHusbandAge65] = useState(
    data.husband_age_65 || false,
  );
  const [wifeAge65, setWifeAge65] = useState(data.wife_age_65 || false);
  const [selfAge65, setSelfAge65] = useState(data.single_age_65 || false);

  const [husbandBlind, setHusbandBlind] = useState(data.husband_blind || false);
  const [wifeBlind, setWifeBlind] = useState(data.wife_blind || false);
  const [selfBlind, setSelfBlind] = useState(data.single_blind || false);

  const sharedExemptionQuestionsInitQ = getInitialQuestions(
    sharedExemptionQuestions,
  );
  const [sharedExemptionQs, setSharedExemptionQs] = useState(
    sharedExemptionQuestionsInitQ,
  );

  const renderBasicInfoForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={basicInfo}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfo.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfo(questions);
        }}
        ref={basicInfoForm}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to claim withholding personal exemptions.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </Flex>
  );

  const renderPersonalExemptions = (isReviewStep) => (
    <Flex column mb="12px">
      <S.QuestionLabel>
        The personal exemption for married individuals filing jointly is
        $12,000. Enter that part of $12,000 claimed by you in multiples of $500.
      </S.QuestionLabel>
      <S.QuestionWrapper>
        <Flex>
          <S.Input
            type="text"
            value={personalExemption || ''}
            onChange={(e) => setPersonalExemption(e.target.value)}
            disabled={isReviewStep}
            hasError={personalExemptionError}
            style={{ borderColor: personalExemptionError ? 'red' : '' }}
          />
        </Flex>
      </S.QuestionWrapper>
      {personalExemptionError && isExemptFromWithholding && (
        <Flex justifyContent="flex-start">
          <Text style={{ marginBottom: '10px' }} color="red">
            Please enter a value, less than or equal to $12,000, that is a
            multiple of $500.
          </Text>
        </Flex>
      )}
    </Flex>
  );

  const [serviceMember, setServiceMember] = useState(
    data.exemption_from_withholding || false,
  );

  const renderClaimExemptionsForm = (isReviewStep) => (
    <Flex column>
      {basicInfo[6].answer.answer.marital_status === 'married_spouse_employed'
        ? renderPersonalExemptions(isReviewStep)
        : ''}
      <Flex column mb="12px">
        <S.QuestionLabel>
          You may claim $1,500 for each dependent, other than for taxpayer and
          spouse, who receives the chief support from you and who qualifies as a
          dependent for federal income tax purposes. Enter the number of
          dependents claimed.
        </S.QuestionLabel>
        <S.QuestionWrapper>
          <Flex>
            <S.Input
              type="text"
              value={numDependents || ''}
              onChange={(e) => setNumDependents(e.target.value)}
              disabled={isReviewStep}
              style={{
                borderColor:
                  numDependentsError || numDependentsValueError ? 'red' : '',
              }}
            />
          </Flex>
        </S.QuestionWrapper>
        {numDependentsError && isExemptFromWithholding && (
          <Flex justifyContent="flex-start" mt="10px" mb="5px">
            <Text color="red">
              To qualify as head of family, you must be single and have at least
              one dependent living in the home with you.
            </Text>
          </Flex>
        )}
        {numDependentsValueError && isExemptFromWithholding && (
          <Flex justifyContent="flex-start" mt="10px" mb="5px">
            <Text color="red">Please enter a valid response</Text>
          </Flex>
        )}
      </Flex>
      <Text>
        Additional exemptions of $1,500 may be claimed by either taxpayer or
        spouse or both if either or both have reached the age of 65 before the
        close of the taxable year, or either taxpayer or spouse or both if
        either or both are blind. Select the applicable blocks for the
        exemptions you want to claim.
      </Text>
      <S.QuestionLabel mb="7px">Age 65 or older</S.QuestionLabel>
      <S.QuestionWrapper mb="20px">
        <label>
          <Flex alignItems="center">
            <S.Input
              type="checkbox"
              value={husbandAge65}
              checked={
                basicInfo[6].answer.answer.marital_status === 'single'
                || basicInfo[6].answer.answer.marital_status === 'head_of_family'
                  ? false
                  : husbandAge65
              }
              disabled={
                isReviewStep
                || basicInfo[6].answer.answer.marital_status === 'single'
                || basicInfo[6].answer.answer.marital_status === 'head_of_family'
              }
              width="auto"
              onChange={(e) => {
                setHusbandAge65(e.target.checked);
              }}
            />
            <Box ml={2} />
            Husband
          </Flex>
        </label>
        <label>
          <Flex alignItems="center">
            <S.Input
              type="checkbox"
              value={wifeAge65}
              checked={
                basicInfo[6].answer.answer.marital_status === 'single'
                || basicInfo[6].answer.answer.marital_status === 'head_of_family'
                  ? false
                  : wifeAge65
              }
              disabled={
                isReviewStep
                || basicInfo[6].answer.answer.marital_status === 'single'
                || basicInfo[6].answer.answer.marital_status === 'head_of_family'
              }
              width="auto"
              onChange={(e) => {
                setWifeAge65(e.target.checked);
              }}
            />
            <Box ml={2} />
            Wife
          </Flex>
        </label>
        <label>
          <Flex alignItems="center">
            <S.Input
              type="checkbox"
              value={selfAge65}
              checked={
                basicInfo[6].answer.answer.marital_status !== 'single'
                && basicInfo[6].answer.answer.marital_status !== 'head_of_family'
                  ? false
                  : selfAge65
              }
              disabled={
                isReviewStep
                || (basicInfo[6].answer.answer.marital_status !== 'single'
                  && basicInfo[6].answer.answer.marital_status
                    !== 'head_of_family')
              }
              width="auto"
              onChange={(e) => {
                setSelfAge65(e.target.checked);
              }}
            />
            <Box ml={2} />
            Single
          </Flex>
        </label>
      </S.QuestionWrapper>
      <S.QuestionLabel mb="7px">Blind</S.QuestionLabel>
      <S.QuestionWrapper mb="20px">
        <label>
          <Flex alignItems="center">
            <S.Input
              type="checkbox"
              value={husbandBlind}
              checked={
                basicInfo[6].answer.answer.marital_status === 'single'
                || basicInfo[6].answer.answer.marital_status === 'head_of_family'
                  ? false
                  : husbandBlind
              }
              disabled={
                isReviewStep
                || basicInfo[6].answer.answer.marital_status === 'single'
                || basicInfo[6].answer.answer.marital_status === 'head_of_family'
              }
              width="auto"
              onChange={(e) => {
                setHusbandBlind(e.target.checked);
              }}
            />
            <Box ml={2} />
            Husband
          </Flex>
        </label>
        <label>
          <Flex alignItems="center">
            <S.Input
              type="checkbox"
              value={wifeBlind}
              checked={
                basicInfo[6].answer.answer.marital_status === 'single'
                || basicInfo[6].answer.answer.marital_status === 'head_of_family'
                  ? false
                  : wifeBlind
              }
              disabled={
                isReviewStep
                || basicInfo[6].answer.answer.marital_status === 'single'
                || basicInfo[6].answer.answer.marital_status === 'head_of_family'
              }
              width="auto"
              onChange={(e) => {
                setWifeBlind(e.target.checked);
              }}
            />
            <Box ml={2} />
            Wife
          </Flex>
        </label>
        <label>
          <Flex alignItems="center">
            <S.Input
              type="checkbox"
              value={selfBlind}
              checked={
                basicInfo[6].answer.answer.marital_status !== 'single'
                && basicInfo[6].answer.answer.marital_status !== 'head_of_family'
                  ? false
                  : selfBlind
              }
              disabled={
                isReviewStep
                || (basicInfo[6].answer.answer.marital_status !== 'single'
                  && basicInfo[6].answer.answer.marital_status
                    !== 'head_of_family')
              }
              width="auto"
              onChange={(e) => {
                setSelfBlind(e.target.checked);
              }}
            />
            <Box ml={2} />
            Single
          </Flex>
        </label>
      </S.QuestionWrapper>
      <FormConsumer
        questions={sharedExemptionQs}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = sharedExemptionQs.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setSharedExemptionQs(questions);
        }}
        ref={exemptionsForm}
      />
      <S.QuestionLabel mb="7px">Exemption from withholding</S.QuestionLabel>
      <S.QuestionWrapper mb="20px">
        <label>
          <Flex alignItems="center">
            <S.Input
              type="checkbox"
              value={serviceMember}
              checked={serviceMember}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setServiceMember(e.target.checked);
              }}
            />
            <Box ml={2} />
            Check this box if you meet the conditions set forth under the
            Service Member Civil Relief, as amended by the Military Spouses
            Residency Relief Act, and have no Mississippi tax liability.
          </Flex>
        </label>
      </S.QuestionWrapper>
    </Flex>
  );

  const calculateTotalExemptions = () => {
    const ms = basicInfo[6].answer.answer.marital_status;
    let personalExemptionAmount;
    if (ms === 'single') {
      personalExemptionAmount = 6000;
    } else if (ms === 'married_spouse_unemployed') {
      personalExemptionAmount = 12000;
    } else if (ms === 'married_spouse_employed') {
      personalExemptionAmount = personalExemption;
    } else {
      personalExemptionAmount = 9500;
    }

    const dependentsAmt = numDependents || 0;
    const row4 = parseInt(dependentsAmt) * 1500;

    let numBoxesChecked = 0;
    if (husbandAge65) numBoxesChecked += 1;
    if (wifeAge65) numBoxesChecked += 1;
    if (selfAge65) numBoxesChecked += 1;
    if (husbandBlind) numBoxesChecked += 1;
    if (wifeBlind) numBoxesChecked += 1;
    if (selfBlind) numBoxesChecked += 1;

    const row5 = numBoxesChecked * 1500;

    return parseInt(personalExemptionAmount) + row4 + row5;
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {isExemptFromWithholding && renderClaimExemptionsForm()}
        </Flex>
        {viewResultAsManager
          && companyStaff.pii_permission === 'full_access' && (
            <Flex column mt={10} mb={10}>
              <Flex alignItems="center">
                <Text mr={16}>Download signed documents</Text>
                <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                  <img src="/j/images/onboarding/download.svg" />
                </a>
              </Flex>
            </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (basicInfoForm && basicInfoForm.current) {
              basicInfoForm.current.validate((error) => {
                if (error) err = error;
              });
            }

            if (exemptionsForm && exemptionsForm.current) {
              exemptionsForm.current.validate((error) => {
                if (error) err = error;
              });
            }

            if (isExemptFromWithholding) {
              if (personalExemption !== undefined) {
                const exemptionValue = personalExemption;
                if (exemptionValue % 500 !== 0 || exemptionValue > 12000) {
                  err = true;
                  setPersonalExemptionError(true);
                } else {
                  setPersonalExemptionError(false);
                }
              }
              if (
                basicInfo[6].answer.answer.marital_status
                  === 'head_of_family'
                && numDependents === '0'
              ) {
                err = true;
                setNumDependentsError(true);
              } else {
                setNumDependentsError(false);
              }
              if (!/^\d*$/.test(numDependents)) {
                err = true;
                setNumDependentsValueError(true);
              } else {
                setNumDependentsValueError(false);
              }
            }
            if (err) {
            } else {
              // No error, proceed to save
              const basicInfoData = basicInfo.reduce(
                (acc, question) => ({ ...acc, ...question.answer.answer }),
                {},
              );

              const exemptionStatusData = {
                is_exempt: isExemptFromWithholding,
              };

              let personalExemptionData;
              let sharedExemptionData;
              let serviceMemberData;
              let totalExemptionsTemp;
              let dependentsData;

              let husbandAge65Temp = false;
              let wifeAge65Temp = false;
              let selfAge65Temp = false;
              let husbandBlindTemp = false;
              let wifeBlindTemp = false;
              let selfBlindTemp = false;

              if (isExemptFromWithholding) {
                if (
                  basicInfo[6].answer.answer.marital_status === 'single'
                  || basicInfo[6].answer.answer.marital_status === 'head_of_family'
                ) {
                  selfAge65Temp = selfAge65;
                  selfBlindTemp = selfBlind;
                  husbandAge65Temp = false;
                  wifeAge65Temp = false;
                  husbandBlindTemp = false;
                  wifeBlindTemp = false;
                } else {
                  if (
                    basicInfo[6].answer.answer.marital_status
                    === 'married_spouse_employed'
                  ) {
                    personalExemptionData = {
                      personal_exemption: personalExemption,
                    };
                  } else {
                    personalExemptionData = { personal_exemption: '' };
                  }
                  selfAge65Temp = false;
                  selfBlindTemp = false;
                  husbandAge65Temp = husbandAge65;
                  wifeAge65Temp = wifeAge65;
                  husbandBlindTemp = husbandBlind;
                  wifeBlindTemp = wifeBlind;
                }
                sharedExemptionData = sharedExemptionQs.reduce(
                  (acc, question) => ({ ...acc, ...question.answer.answer }),
                  {},
                );
                serviceMemberData = {
                  exemption_from_withholding: serviceMember,
                };
                dependentsData = { dependents: numDependents };
                totalExemptionsTemp = calculateTotalExemptions();
              } else {
                setPersonalExemption('');
                personalExemptionData = { personal_exemption: '' };
                sharedExemptionData = resetInitialQuestions(
                  sharedExemptionQuestions,
                ).reduce(
                  (acc, question) => ({ ...acc, ...question.answer.answer }),
                  {},
                );
                serviceMemberData = {
                  exemption_from_withholding: '',
                };
                totalExemptionsTemp = 0;
              }

              const exemptionCalculations = {
                total_exemptions: totalExemptionsTemp,
              };
              const additionalExemptionData = {
                husband_age_65: husbandAge65Temp,
                wife_age_65: wifeAge65Temp,
                single_age_65: selfAge65Temp,
                husband_blind: husbandBlindTemp,
                wife_blind: wifeBlindTemp,
                single_blind: selfBlindTemp,
              };
              const data = {
                ...basicInfoData,
                ...exemptionStatusData,
                ...additionalExemptionData,
                ...personalExemptionData,
                ...sharedExemptionData,
                ...dependentsData,
                ...exemptionCalculations,
                ...serviceMemberData,
              };

              saveFormData(data, () => {
                setCurrentStep(STEPS.step2);
                scrollTop();
              });
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {isExemptFromWithholding && renderClaimExemptionsForm(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}
export default LegacyForm;
